import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom/cjs/react-router-dom";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import _debounce from "lodash/debounce";
import {
  activityDetailList,
  setActivityDetailData,
  setAppActivityData
} from "../../../AxiosRtk/AppActivity";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { CustomTooltip } from "../../Dashboard/DashboardGraph/ScreenTime";
import useLangTranslation from "../../../../customHooks/languageProvider";
import Select from "react-select";
import useDurationOption from "../../../../customHooks/durationOption";
import { handleStartDateCommon } from "../../../../../utils/helper";
import DateFilter from "../../../../shared/DateFilter/DateFilter";
import KidImage from "../../../../shared/KidImage/KidImage";
import CommonStatus from "../../Common/CommonStatus";
import useDeviceStatus from "../../../../customHooks/deviceStatusProvider";
import useFirebase from "../../../../../useFirebase";
import { useDashboardDataProvider } from "../../../../customHooks/dashboardDataProvider";

const ActivityDetails = (props) => {
  const { durationOptionsFunction } = useDurationOption();
  const { getLangTranslation } = useLangTranslation();
  const { handleDeviceSelection } = useDashboardDataProvider();
  const { getCollectionData } = useFirebase();
  const dispatch = useDispatch();
  const { activityDetailData, appActivityData } = useSelector(
    ({ appActivity }) => appActivity
  );
  const [selectedDuration, setSelectedDuration] = useState(1);
  const [deviceStatus, setDeviceStatus] = useState("");

  const { getDeviceStatusData } = useDeviceStatus(
    deviceStatus,
    setDeviceStatus
  );

  const durationOptions = durationOptionsFunction();
  const history = useHistory();

  const [startDate, setStartDate] = useState(
    moment().startOf("day").format("YYYY-MM-DD HH:mm:ss")
  );
  const initialEndDate = moment().locale("en").format("YYYY-MM-DD HH:mm:ss");
  const [endDate, setEndDate] = useState(initialEndDate);
  const { kidList, selectedKidId, kidDevicesList, selectedDeviceData } =
    useSelector(({ dashboard }) => dashboard);
  const selectedKidData = kidList?.find((item) => item?.ID === selectedKidId);

  const handleGoBack = useCallback(() => {
    history.goBack();
  });
  const appID = props.match.params.id;
  const num = Number(appID);

  const appData = appActivityData?.mostUsedApps?.find(
    (item) => item?.appId === num
  );

  function convertSecondsToHMS(seconds) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    return `${hours} h ${minutes} m ${remainingSeconds} s`;
  }

  const convertedTime = convertSecondsToHMS(activityDetailData?.dailyDuration);
  const graphData = activityDetailData?.appGraphData;

  const deviceOptions = useMemo(() => {
    return kidDevicesList?.map((device) => {
      return { value: device?.ID, label: device?.Name };
    });
  }, [kidDevicesList]);

  const debouncedFetchData = useCallback(
    _debounce((payload) => {
      activityDetailList(payload)
        .then((res) => {
          dispatch(setActivityDetailData(res?.data?.result));
        })
        .catch((err) => {
          console.error(
            "App activity catch error: ",
            err?.response?.fdata?.message
          );
        });
    }, 300),
    []
  );

  useEffect(() => {
    if (selectedDeviceData) {
      getDeviceStatusData(selectedDeviceData?.ID);
      getCollectionData(selectedDeviceData?.ID);
    } else {
      dispatch(setAppActivityData(null));
    }
  }, [selectedDeviceData]);

  useEffect(() => {
    if (selectedDeviceData) {
      const payload = {
        deviceId: selectedDeviceData?.ID,
        startDate: startDate,
        endDate: endDate,
        appId: appID,
      };
      debouncedFetchData(payload);
    }
  }, [selectedDeviceData, startDate, endDate, appID]);

  const convertSecondsToMinutes = (seconds) => {
    return Math.floor(seconds / 60); // Convert seconds to minutes
  };

  const dataWithMinutes = graphData?.map((item) => ({
    ...item,
    'App Duration': convertSecondsToMinutes(item.totalAppDuration),
  }));
  const AdminSetRuleLanguageData = useSelector(({ setRules }) => setRules);
  const { appLanguageId } = useSelector(({ misc }) => misc);
  const lang =
    AdminSetRuleLanguageData?.AdminSetRuleLanguageData?.languages?.find(
      (item) => item?.id === appLanguageId
    );

  return (
    <>
      <div className="card mb-6">
        <div className="card-body">
          <div className="inline-list-wrap separator space-24">
            <div className="list-item">
              <div className="list-content">
                <div className="d-inline-flex align-items-center lh-1">
                  <Link
                    to="/"
                    className="text-blue-900 fs-5"
                    onClick={handleGoBack}
                  >
                    <i
                      className={`${lang?.isRTL === 1
                        ? "icon-arrow-right"
                        : "icon-arrow-left"
                        }`}
                    />
                  </Link>
                  <h3 className="text-blue-900 mb-0"> {getLangTranslation("CHILD_DETAIL_APP_ACTIVITY", "Apps activity")}</h3>
                </div>
              </div>
            </div>
            <div className="list-item">
              <KidImage imageUrl={selectedKidData?.image} gender={selectedDeviceData?.gender} />
              <div>
                <h5 className="text-blue-900 mb-0">
                  {selectedKidData?.childName}
                </h5>
                <CommonStatus status={deviceStatus} />
              </div>
            </div>
            <div className="list-item content-300 w-100">
              <div className="select-with-icon">
                <span className="icon-wrap icon-blue pt-cursor border-0">
                  <i className="icon-mobile fs-5" />
                </span>

                <Select
                  className="custom-select"
                  classNamePrefix="custom-select"
                  placeholder="Select"
                  onChange={handleDeviceSelection}
                  options={deviceOptions}
                  value={
                    deviceOptions?.find(
                      (item) => item?.value === selectedDeviceData?.ID
                    ) || ""
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="tab-content" id="nav-tabContent">
        <div
          className="tab-pane fade show active"
          id="Activity"
          role="tabpanel"
          aria-labelledby="activityTab"
        >
          <div className="card border-0 mb-6">
            <div className="card-body">
              <div className="d-flex flex-wrap align-items-center gap-3 mb-6">
                <span className="icon-wrap  border-0 p-0">
                  <img src={appData?.icon} alt="" />
                </span>
                <h5
                  className={`d-inline-flex align-items-center gap-2 mb-0 ${lang?.isRTL === 1 ? "ms-auto" : "me-auto"
                    }`}
                >
                  {appData?.appName}
                  <span className="text-small text-gray">{convertedTime}</span>
                </h5>

                <DateFilter
                  startDate={startDate}
                  endDate={endDate}
                  setEndDate={setEndDate}
                  setStartDate={setStartDate}
                  selectedDuration={selectedDuration}
                />
                <Select
                  className="custom-select col-lg-3"
                  classNamePrefix="custom-select"
                  placeholder="Select"
                  onChange={(val) => {
                    handleStartDateCommon(
                      val,
                      setStartDate,
                      setSelectedDuration,
                      setEndDate
                    );
                  }}
                  options={durationOptions}
                  value={
                    durationOptions?.find(
                      (item) => item?.value === selectedDuration
                    ) || ""
                  }
                />
              </div>
              <ResponsiveContainer width="100%" height={520}>
                <BarChart
                  width={1000}
                  height={520}
                  data={dataWithMinutes}
                >
                  <XAxis dataKey="hour" />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="App Duration" fill="#87CEEB" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ActivityDetails;
